import React from "react";
import { ShieldCheck, Scale, Gavel, AlertTriangle, ShieldAlert, Landmark } from "lucide-react";

/**
 * Prominent legal-status badge shown near the name on cards + detail pages.
 * Reads the `disposition` object attached by the backend
 * ({ category, sub, badge_label, is_public }).
 *
 * Never derives status from anything else — we display exactly what the
 * backend classifier said. If disposition is missing (older cached client),
 * we render nothing rather than guessing.
 *
 * size: "sm" (card) | "md" (detail hero)
 */
export default function StatusBadge({ disposition, size = "sm" }) {
  if (!disposition || !disposition.badge_label) return null;

  const sub = disposition.sub;
  const isConvicted = disposition.category === "convicted_registry";

  const config = {
    convicted:   { Icon: Gavel,        bg: "bg-zinc-900",  text: "text-white",   ring: "border-zinc-900" },
    guilty_plea: { Icon: ShieldCheck,  bg: "bg-zinc-900",  text: "text-white",   ring: "border-zinc-900" },
    registered:  { Icon: Landmark,     bg: "bg-zinc-900",  text: "text-white",   ring: "border-zinc-900" },
    charged:     { Icon: AlertTriangle, bg: "bg-amber-500", text: "text-zinc-900", ring: "border-amber-600" },
    indicted:    { Icon: Scale,        bg: "bg-amber-500", text: "text-zinc-900", ring: "border-amber-600" },
    on_trial:    { Icon: Scale,        bg: "bg-amber-500", text: "text-zinc-900", ring: "border-amber-600" },
  }[sub] || { Icon: AlertTriangle, bg: "bg-zinc-500", text: "text-white", ring: "border-zinc-600" };

  const { Icon } = config;

  const pad = size === "md" ? "px-3 py-1.5 text-sm" : "px-2 py-1 text-[10px]";
  const iconSize = size === "md" ? 13 : 11;

  return (
    <span
      className={`inline-flex items-center gap-1.5 border-2 ${config.ring} ${config.bg} ${config.text} ${pad} font-black uppercase tracking-widest whitespace-nowrap`}
      title={
        isConvicted
          ? "This person is part of the Paws on Record Convicted Registry."
          : "PENDING: This case has not been adjudicated. The individual is presumed innocent unless and until proven guilty."
      }
      data-testid={`status-badge-${sub}`}
    >
      <Icon size={iconSize} strokeWidth={2.5} />
      {disposition.badge_label}
    </span>
  );
}
